// Generic Übersicht für Laufräder / Baugruppen / Montage.
// Konfiguration über `cfg` — zeigt Spezialfelder als Zusatzspalten optional.
//
// Drückteile bleibt separat (eigene Übersicht mit eigenen Spezialfeldern).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';
import { moduleApi, type ModuleItem } from '../lib/moduleApi';
import { StandCell } from '../components/StandCell';
import { HeuteModulWidget } from '../components/HeuteWidget';
import { AbteilungBadge } from '../components/AbteilungBadge';
import BeistelteileIndicator, { useBeistelteileSummary } from '../components/BeistelteileIndicator';
import { VorkasseLockBadge, isVorkasseGesperrt } from '../components/VorkasseBanner';
import type { BeistelteilModul } from '../lib/api';
import { achtungKommentar } from '../lib/achtung';

type SortDir = 'asc' | 'desc';
type SortKey = 'bestellnr' | 'dv_nummer' | 'position' | 'kunde' | 'bezeichnung'
             | 'zeichnungs_nr' | 'stueck' | 'lieferdatum' | 'kw' | 'gesamtzeit'
             | 'stand' | 'sb' | 'priority' | 'fertig';

export interface ModuleExtraColumn {
  key: string;
  label: string;
  render?: (item: ModuleItem) => React.ReactNode;
}

export interface ModuleCfg {
  path: 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant';
  label: string;            // 'Laufräder'
  breadcrumb: string;       // 'Produktion'
  extraColumns?: ModuleExtraColumn[];
}

export function ModuleUebersicht({ cfg }: { cfg: ModuleCfg }) {
  const client = moduleApi(cfg.path);

  const [search, setSearch] = useState('');
  const [fertigFilter, setFertigFilter] = useState<'offen' | 'fertig' | 'alle'>('offen');
  const [sort, setSort] = useState<SortKey>('lieferdatum');
  const [dir,  setDir]  = useState<SortDir>('asc');
  const [offset, setOffset] = useState(0);
  const [kundeFilter, setKundeFilter] = useState('');
  const [sbFilter, setSbFilter] = useState('');
  const [standFilter, setStandFilter] = useState('');
  const [prioFilter, setPrioFilter] = useState('');

  const kunden     = useApi(() => api.lookup.kunden(), []);
  const users      = useApi(() => api.lookup.users(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);

  const list = useApi(
    () => client.list({
      q: search || undefined,
      kunde_id: kundeFilter || undefined,
      sachbearbeiter_id: sbFilter || undefined,
      stand_id: standFilter || undefined,
      priority: prioFilter || undefined,
      fertig: fertigFilter === 'offen' ? 0 : fertigFilter === 'fertig' ? 1 : undefined,
      sort, dir, limit: 50, offset,
    }),
    [search, kundeFilter, sbFilter, standFilter, prioFilter, fertigFilter, sort, dir, offset]
  );

  const beistelteileSummary = useBeistelteileSummary(
    cfg.path as BeistelteilModul,
    list.data?.data.map(d => d.id) ?? []
  );

  const toggleSort = (key: SortKey) => {
    if (sort === key) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDir('asc'); }
    setOffset(0);
  };

  const extraCols = cfg.extraColumns ?? [];
  const totalCols = 12 + extraCols.length;

  return (
    <div className="p-8">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{cfg.breadcrumb}</div>
          <h1 className="text-2xl font-bold text-slate-900">{cfg.label} – Übersicht</h1>
        </div>
        <Link to={`/${cfg.path}/neu`} className="btn-primary">+ Neuer Auftrag</Link>
      </header>

      <HeuteModulWidget modul={cfg.path} label={cfg.label} />

      {/* Globale Suche + Status-Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-3">
        <input type="search"
               placeholder={`🔍 Suche: DV-Nr, Bestellnr, Bezeichnung, Zeichnungs-Nr, Kunde …`}
               value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }}
               className="flex-1 input" />
        <select value={kundeFilter} onChange={e => { setKundeFilter(e.target.value); setOffset(0); }}
                className="input w-44">
          <option value="">Alle Kunden</option>
          {kunden.data?.data.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        <select value={sbFilter} onChange={e => { setSbFilter(e.target.value); setOffset(0); }}
                className="input w-40">
          <option value="">Alle SB</option>
          {users.data?.data.filter(u => u.kurzel !== 'SYS').map(u =>
            <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={standFilter} onChange={e => { setStandFilter(e.target.value); setOffset(0); }}
                className="input w-40">
          <option value="">Alle Stände</option>
          {standWerte.data?.data.map(s => <option key={s.id} value={s.id}>{s.wert}</option>)}
        </select>
        <select value={prioFilter} onChange={e => { setPrioFilter(e.target.value); setOffset(0); }}
                className="input w-28">
          <option value="">Prio</option>
          <option value="normal">Normal</option>
          <option value="eilig">Eilig</option>
          <option value="kritisch">Kritisch</option>
        </select>
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {(['offen','fertig','alle'] as const).map(v => (
            <button key={v} onClick={() => { setFertigFilter(v); setOffset(0); }}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      fertigFilter === v ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}>
              {v === 'offen' ? 'Offen' : v === 'fertig' ? 'Fertig' : 'Alle'}
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500 whitespace-nowrap px-2">
          {list.data ? `${list.data.pagination.total}` : '…'}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[1400px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr className="text-xs text-slate-600 uppercase">
              <Th sortKey="bestellnr"    label="Bestellnr"    sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="dv_nummer"    label="DV-Nr"        sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="position"     label="Pos"          sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="kunde"        label="Kunde"        sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="bezeichnung"  label="Bezeichnung"  sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="zeichnungs_nr" label="Zeichn.-Nr"  sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="stueck"       label="Stk"          sort={sort} dir={dir} onClick={toggleSort} align="right" />
              <Th sortKey="lieferdatum"  label="Liefertermin" sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="kw"           label="KW"           sort={sort} dir={dir} onClick={toggleSort} align="center" />
              <Th sortKey="gesamtzeit"   label="Zeit"         sort={sort} dir={dir} onClick={toggleSort} align="right" />
              <Th sortKey="stand"        label="Stand"        sort={sort} dir={dir} onClick={toggleSort} />
              <th className="px-4 py-2 font-medium text-left">Aktuell</th>
              <Th sortKey="sb"           label="SB"           sort={sort} dir={dir} onClick={toggleSort} />
              {extraCols.map(c => (
                <th key={c.key} className="px-4 py-2 font-medium text-left">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.loading && <tr><td colSpan={totalCols} className="text-center py-12 text-slate-500">Lädt …</td></tr>}
            {list.data && list.data.data.length === 0 && (
              <tr><td colSpan={totalCols} className="text-center py-12 text-slate-500">Keine Ergebnisse</td></tr>
            )}
            {list.data?.data.map(d => {
              const achtung = achtungKommentar(d.kommentar);
              const gesperrt = isVorkasseGesperrt(d.vorkasse, d.vorkasse_bezahlt_am);
              return (
              <tr key={d.id} className={gesperrt ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : achtung ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'}>
                <td className="px-4 py-2 font-mono text-xs">
                  <div className="flex items-center gap-1">
                    {d.bestellnr}
                    <BeistelteileIndicator summary={beistelteileSummary[String(d.id)]} />
                    <VorkasseLockBadge vorkasse={d.vorkasse} bezahltAm={d.vorkasse_bezahlt_am} />
                  </div>
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  <Link to={`/${cfg.path}/${d.id}`} className="text-brand hover:underline">
                    {d.dv_nummer ?? '—'}
                  </Link>
                </td>
                <td className="px-4 py-2">{d.position ?? '—'}</td>
                <td className="px-4 py-2">{d.kunde_name ?? '—'}</td>
                <td className="px-4 py-2 max-w-[280px]" title={d.bezeichnung ?? ''}>
                  <div className="truncate">
                    {d.bezeichnung ?? '—'}
                    {d.priority === 'eilig'    && <span className="ml-2 text-xs font-bold text-red-600">EILIG</span>}
                    {d.priority === 'kritisch' && <span className="ml-2 text-xs font-bold text-red-800">KRITISCH</span>}
                  </div>
                  {d.kommentar && (achtung ? (
                    <div className="text-xs font-bold text-amber-700 whitespace-normal mt-0.5">⚠ {d.kommentar}</div>
                  ) : (
                    <div className="text-xs text-slate-500 whitespace-normal mt-0.5">💬 {d.kommentar}</div>
                  ))}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{d.zeichnungs_nr ?? '—'}</td>
                <td className="px-4 py-2 text-right">{d.stueck}</td>
                <td className="px-4 py-2 whitespace-nowrap"><LieferDatum iso={d.lieferdatum} fertig={d.fertig === 1} /></td>
                <td className="px-4 py-2 text-center">
                  <span className="bg-sky-100 text-sky-900 px-2 py-0.5 rounded text-xs font-semibold">{d.kw}</span>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{d.gesamtzeit}h</td>
                <td className="px-4 py-2 min-w-[160px]">
                  <StandCell
                    drueckteilId={d.id}
                    currentStandId={d.stand_id}
                    currentStandWert={d.stand_wert}
                    currentStandFarbe={d.stand_farbe}
                    updatedAt={d.updated_at}
                    standWerte={standWerte.data?.data ?? []}
                    onChanged={() => list.reload()}
                    modulePath={cfg.path} />
                </td>
                <td className="px-4 py-2">
                  <AbteilungBadge session={d.current_session as string | null | undefined} />
                </td>
                <td className="px-4 py-2">
                  <span className="bg-slate-100 rounded px-2 py-0.5 text-xs">{d.sachbearbeiter_name ?? '—'}</span>
                </td>
                {extraCols.map(c => (
                  <td key={c.key} className="px-4 py-2">
                    {c.render ? c.render(d) : renderField(d[c.key])}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>

        {list.data && list.data.pagination.total > list.data.pagination.limit && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
            <div>
              Zeige {offset + 1}–{Math.min(offset + list.data.pagination.limit, list.data.pagination.total)} von {list.data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - 50))}>‹ Zurück</button>
              <button className="btn-secondary" disabled={!list.data.pagination.hasMore}
                      onClick={() => setOffset(offset + 50)}>Weiter ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderField(v: unknown): React.ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-slate-400">—</span>;
  if (typeof v === 'boolean' || v === 0 || v === 1) return (v === 1 || v === true) ? '✓' : '—';
  return String(v);
}

function Th({ label, sortKey, sort, dir, onClick, align = 'left' }: {
  label: string; sortKey: SortKey; sort: SortKey; dir: SortDir;
  onClick: (key: SortKey) => void; align?: 'left' | 'right' | 'center';
}) {
  const active = sort === sortKey;
  const arrow  = !active ? '↕' : dir === 'asc' ? '▲' : '▼';
  return (
    <th onClick={() => onClick(sortKey)}
        className={`px-4 py-2 font-medium cursor-pointer select-none hover:bg-slate-100 ${
          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
        } ${active ? 'text-brand' : ''}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'text-brand' : 'text-slate-300'}`}>{arrow}</span>
      </span>
    </th>
  );
}

function LieferDatum({ iso, fertig }: { iso: string; fertig: boolean }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86400000);
  const formatted = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  if (fertig) return <span className="text-slate-400">{formatted}</span>;
  let cls = 'text-slate-700';
  let badge: string | null = null;
  if (days < 0)        { cls = 'text-red-700 font-semibold';    badge = `${Math.abs(days)}T verspätet`; }
  else if (days <= 3)  { cls = 'text-orange-700 font-semibold'; badge = days === 0 ? 'HEUTE' : days === 1 ? 'morgen' : `in ${days}T`; }
  else if (days <= 7)  { cls = 'text-amber-700'; }
  return (
    <span className={cls} title={badge ?? `in ${days} Tagen`}>
      {formatted}{badge && <span className="ml-1 text-[10px] opacity-75">· {badge}</span>}
    </span>
  );
}
